/**
 * Select — the app's custom dropdown, ported from the enhancer in views/layout.ejs.
 *
 * The EJS app does not use a bare <select> anywhere: a script wraps each one at runtime in
 * `.sel` > (`select` hidden + `button.sel-btn` + `.sel-menu` of `.sel-opt` rows). That structure is what
 * public/app.css styles and what the browser suite drives (`click .sel-btn`, then `.sel-opt`), so the SPA has
 * to render the same thing rather than a native control that looks different to every test and to the reader.
 *
 * The native <select> stays in the DOM (hidden) and is still the form value: a caller wiring this into a form
 * gets the same `name`/`value` submission the EJS page has, and a screen reader sees a real select.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

export type SelectOption = { value: string; label: string; disabled?: boolean };

export type SelectProps = {
  id?: string;
  name?: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  ariaLabel?: string;
  /** Font size for the button: the EJS enhancer copies it off the select, and headings use a larger one. */
  fontSize?: number | string;
  fontWeight?: number | string;
  /**
   * The height an EJS `<select style="height: 30px">` declares. views/layout.ejs's enhancer copies it onto the
   * button and zeroes the button's vertical padding, which is what keeps the page's own header row 35px tall
   * instead of the 44px a 22px font with 8px padding produces.
   */
  height?: number;
  style?: React.CSSProperties;
  className?: string;
};

export function Select({
  id,
  name,
  value,
  options,
  onChange,
  ariaLabel,
  fontSize,
  fontWeight,
  height,
  style,
  className,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrap = useRef<HTMLSpanElement | null>(null);
  const btn = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const [place, setPlace] = useState<{ top: number; left: number; minWidth: number } | null>(null);

  const menuId = useId();
  const current = options.find((o) => o.value === value) ?? options[0];

  // Outside click and Escape close the menu — the same two ways out the enhancer offers.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The menu is a FIXED overlay, as PFOverlay renders it on the EJS pages: `left: 0; right: 0` pinned it to
  // the trigger, so "Sort by: Market Value" was clipped inside a 139px box, and an absolutely positioned menu
  // is clipped by any scrolling ancestor. Its width is its own content with the trigger width as a floor
  // (layout.ejs: `menu.style.minWidth = btn width`), then clamped into the viewport.
  // useLayoutEffect, not useEffect: the first frame after a click would otherwise paint the panel at its
  // placeholder position and a measurement taken then reads -9999.
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const where = () => {
      const b = btn.current;
      if (!b) return;
      const r = b.getBoundingClientRect();
      const h = menu.current ? menu.current.offsetHeight : 0;
      const w = menu.current ? menu.current.offsetWidth : r.width;
      const margin = 8;
      const gap = 4;
      let top = r.bottom + gap;
      if (h && top + h > window.innerHeight - margin) {
        const above = r.top - gap - h;
        top = above >= margin ? above : Math.max(margin, window.innerHeight - margin - h);
      }
      const left = Math.min(Math.max(margin, r.left), Math.max(margin, window.innerWidth - margin - w));
      setPlace({ top, left, minWidth: Math.round(r.width) });
    };
    where();
    window.addEventListener('resize', where);
    window.addEventListener('scroll', where, true);
    return () => {
      window.removeEventListener('resize', where);
      window.removeEventListener('scroll', where, true);
    };
  }, [open]);

  const choose = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  return (
    // `open` on the WRAPPER drives both rules in public/app.css: .sel.open .sel-menu { display: block } and the
    // chevron flip. The menu itself must carry no inline display, or it would fight that rule.
    // The caller's class belongs on the SELECT, as in the EJS markup: `.tax-year { font-size: 22px }` is the
    // style the enhancer reads off the select and copies to the button. Putting it on the wrapper instead made
    // the wrapper's own line box 22px and the header row 9px taller than the EJS page's.
    <span className={['sel', open ? 'open' : ''].filter(Boolean).join(' ')} ref={wrap} style={{ position: 'relative', ...style }}>
      <select
        id={id}
        name={name}
        className={className}
        style={{ display: 'none', ...(height ? { height, lineHeight: `${height}px` } : null) }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-hidden="true"
        tabIndex={-1}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        ref={btn}
        className="sel-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={ariaLabel}
        style={{
          fontSize,
          fontWeight,
          ...(height ? { height, lineHeight: `${height}px`, paddingTop: 0, paddingBottom: 0 } : null),
        }}
        aria-activedescendant={open && active >= 0 ? `${menuId}-opt-${active}` : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          // The button keeps focus and `aria-activedescendant` names the highlighted option — the listbox
          // pattern. Moving real focus into .sel-opt would leave a list of rows that TAB walks out of.
          const move = (next: number) => {
            setOpen(true);
            setActive(Math.max(0, Math.min(options.length - 1, next)));
          };
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            // Closed: open on the CURRENT value, the way a native select does, not on the first row.
            const from = open ? (active < 0 ? (e.key === 'ArrowDown' ? -1 : options.length) : active)
                              : Math.max(0, options.findIndex((o) => o.value === value));
            setOpen(true);
            setActive(Math.max(0, Math.min(options.length - 1, from + (e.key === 'ArrowDown' ? 1 : -1))));
          }
          if (e.key === 'Home' && open) { e.preventDefault(); move(0); }
          if (e.key === 'End' && open) { e.preventDefault(); move(options.length - 1); }
          if (e.key === 'Tab') setOpen(false);
          if (e.key === 'Enter' && open && active >= 0) {
            e.preventDefault();
            choose(options[active].value);
          }
          if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            const hit = options.findIndex((o) => o.label.toLowerCase().startsWith(e.key.toLowerCase()));
            if (hit >= 0) move(hit);
          }
        }}
      >
        {current ? current.label : ''}
      </button>
      <div
        ref={menu}
        className="sel-menu"
        role="listbox"
        id={menuId}
        style={{
          position: 'fixed',
          top: place ? place.top : -9999,
          left: place ? place.left : -9999,
          minWidth: place ? place.minWidth : undefined,
          width: 'max-content',
          maxWidth: 'calc(100vw - 16px)',
        }}
      >
        {options.map((o, i) => (
          <div
            key={o.value}
            id={`${menuId}-opt-${i}`}
            className={'sel-opt' + (o.value === value ? ' sel-active' : '')}
            role="option"
            aria-selected={o.value === value}
            tabIndex={-1}
            onMouseEnter={() => setActive(i)}
            onClick={() => choose(o.value)}
          >
            {o.label}
          </div>
        ))}
      </div>
    </span>
  );
}
